import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../StyledComponents";
import { deleteAccount, updateUserProfile } from "../user/userSlice";

const AccountDetails = () => {
  const dispatch = useDispatch();
  const {
    firstName,
    userLocation,
    userAddress,
    updateStatus,
    updateError,
    id,
  } = useSelector((state) => state.user);
  const [localFirstName, setLocalFirstName] = useState(firstName);
  const [localUserLocation, setLocalUserLocation] = useState(userLocation);
  const [localUserAddress, setLocalUserAddress] = useState(userAddress);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLocalFirstName(firstName);
    setLocalUserLocation(userLocation);
    setLocalUserAddress(userAddress);
  }, [firstName, userLocation, userAddress]);

  useEffect(() => {
    let timeoutId;
    if (updateStatus === "succeeded") {
      setShowSuccess(true);
      timeoutId = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [updateStatus]);

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    dispatch(
      updateUserProfile({
        first_name: localFirstName,
        user_location: localUserLocation,
        user_address: localUserAddress,
      })
    );
  };

  const handleDeleteAccount = () => {
    setOpenDeleteDialog(true);
  };

  const confirmDeleteAccount = () => {
    dispatch(deleteAccount());
    setOpenDeleteDialog(false);
  };

  const handleCopyId = async () => {
    await navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const handleSupportPress = () => {
    const body = encodeURIComponent(
      `[for account ID: ${id}]\n\nHey Smoothspeak support,`
    );
    window.location.href = `mailto:support@smoothspeak.ai?body=${body}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "center" },
        mt: 3,
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            boxShadow: 3,
            p: 4,
            mb: 4,
            backgroundColor: "#1C1C1E",
            color: "#FFFFFF",
          }}
        >
          <CardContent>
            <Typography
              variant="h5"
              gutterBottom
              align="center"
              mb={2}
              color="white"
            >
              Account Details
            </Typography>
            <form onSubmit={handleSaveChanges}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={localFirstName}
                  onChange={(e) => setLocalFirstName(e.target.value)}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.23)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "& .MuiInputBase-input": {
                      color: "white",
                    },
                  }}
                />
                <Box sx={{ height: { xs: 8, sm: 16 } }} />
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={updateStatus === "loading"}
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      color: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  {updateStatus === "loading" ? "Saving..." : "Save Changes"}
                </PrimaryButton>
                <PrimaryButton
                  variant="contained"
                  onClick={handleDeleteAccount}
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                  }}
                >
                  Delete Account
                </PrimaryButton>
                {updateStatus === "failed" && (
                  <Typography color="error" align="center">
                    {typeof updateError === "string"
                      ? updateError
                      : updateError?.detail || "Failed to update profile"}
                  </Typography>
                )}
                {updateStatus === "succeeded" && showSuccess && (
                  <Typography color="success.main" align="center">
                    Profile updated successfully
                  </Typography>
                )}
              </Stack>
            </form>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 2,
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            pt: 2,
          }}
        >
          <Link
            href={`mailto:support@smoothspeak.ai?body=${encodeURIComponent(
              `[for account ID: ${id}]\n\nHey Smoothspeak support,`
            )}`}
            sx={{
              color: "#0066CC",
              textDecoration: "none",
              cursor: "pointer",
              mb: 2,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Contact Support
          </Link>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                fontFamily: "monospace",
                fontSize: 10,
              }}
            >
              Account ID: {id}
            </Typography>
            <IconButton
              onClick={handleCopyId}
              size="small"
              sx={{
                color: "#FF3B30",
                padding: "4px",
              }}
            >
              {copied ? (
                <CheckCircleOutlineIcon
                  fontSize="inherit"
                  sx={{ fontSize: 14 }}
                />
              ) : (
                <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Container>
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: "#2C2C2E",
            color: "#FFFFFF",
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFFFFF" }}>
          Confirm Account Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
            Warning: This action will delete all your account data and cannot be
            reversed. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton onClick={() => setOpenDeleteDialog(false)}>
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={confirmDeleteAccount}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Delete Account
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDetails;
